import { useEffect, useRef, useState } from 'react';

/**
 * Measure a container so charts can be drawn at real pixel width.
 *
 * The alternative — a fixed `viewBox` scaled with `preserveAspectRatio` — scales the TEXT along
 * with the geometry, so axis labels grow on wide screens and shrink on narrow ones, and stroke
 * widths stop meaning what the mark specs say they mean. Measuring costs one observer per chart
 * and keeps 2px lines 2px everywhere.
 */
export function useElementWidth<T extends HTMLElement>(fallback = 640) {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(fallback);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      // Ignore a zero width: a hidden or not-yet-laid-out container would otherwise collapse the
      // chart to nothing and it would never recover once shown.
      if (next && next > 0) setWidth(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}
